import * as React from 'react';
// Import material ui modules.
import {
	Check,
	Close,
} from '@mui/icons-material';
// Import hooks.
import {
	useFormPopupAddPolicyNoteBase,
	UseFormPopupAddPolicyNoteBaseResults,
} from '@app/hooks/view';
// Import props.
import {
	FormPopupAddPolicyNoteProps,
	FormPopupProps,
	NavigationFooterProps,
	ScrollbarProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
	PopupWidth,
} from '@app/enumerations';


interface UseFormPopupAddPolicyNoteParameters {
	id: string;
	props: FormPopupAddPolicyNoteProps,
}

interface UseFormPopupAddPolicyNoteResults {
	submitForm: () => void;
	formPopupAddPolicyNoteBase: UseFormPopupAddPolicyNoteBaseResults;
	applyFormProps: () => FormPopupProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPopupAddPolicyNote = (params: UseFormPopupAddPolicyNoteParameters): UseFormPopupAddPolicyNoteResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidationWithFocus();
		// If form passes validation, proceed to submission.
		if (isValid) {
			params.props.onSave(formPopupAddPolicyNoteBase.noteInput.value,  formPopupAddPolicyNoteBase.createPolicyNoteDocument());
		}
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormPopupProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Add Note',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			isOpen: params.props.isOpen,
			popupWidth: PopupWidth.SMALL,
			onCloseClick: params.props.onClose,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Close',
			leftButtonClick: params.props.onClose,
			leftButtonIcon: Close,
			rightButtonDisabled: false,
			rightButtonLabel: 'Save',
			rightButtonClick: submitForm,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Method to check form validation with focus. 
	 **/
	const checkFormValidationWithFocus = (): boolean => {
		return formPopupAddPolicyNoteBase.checkValidationWithFocus()
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		formPopupAddPolicyNoteBase.flagAllInputsToShowValidations();
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	// Instantiate the 'add policy note popup' form base hook.
	const formPopupAddPolicyNoteBase: UseFormPopupAddPolicyNoteBaseResults = useFormPopupAddPolicyNoteBase({
		id: generateIdForForm(),
		possibleDocCategories: params.props.possibleDocCategories,
		policyTermYears: params.props.policyTermYears,
	});

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// Focus to the first element by default.
		formPopupAddPolicyNoteBase.noteInput.focusToInput();
	}, []);

	// #endregion life-cycle methods.


	return {
		submitForm: submitForm,
		formPopupAddPolicyNoteBase: formPopupAddPolicyNoteBase,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
	};
}

// Export parameters.
export {
	UseFormPopupAddPolicyNoteParameters,
}

// Export results.
export {
	UseFormPopupAddPolicyNoteResults,
}

export default useFormPopupAddPolicyNote;